import { useEffect, useRef, useState } from "react";

import { Popup, type PopupProps } from "semantic-ui-react";

import {
  FocusStackContext,
  createFocusTrap,
  useFocusStack,
} from "./FocusStack";

type AccessiblePopupProps = PopupProps & { trapFocus?: boolean };

const AccessiblePopup = ({ trapFocus, ...props }: AccessiblePopupProps) => {
  const focusStack = useFocusStack();

  const popupRef = useRef<HTMLDivElement>(null);

  const [returnFocusElement, setReturnFocusElement] =
    useState<HTMLElement | null>(null);
  const [internalOpen, setInternalOpen] = useState(props.open ?? false);

  // When the popup is opened, either controlled or uncontrolled, retain state of the current focus and set focus to the first focusable child
  // Within the popup, trap focus to prevent focus from leaving the popup
  // Once closed, return focus to the previously focused element
  useEffect(() => {
    if (!trapFocus) {
      return;
    }
    if (
      props.open !== undefined ? props.open === true : internalOpen === true
    ) {
      setReturnFocusElement(document.activeElement as HTMLElement);

      const focusTrap = createFocusTrap(popupRef.current);
      focusStack.insertFocusTrap(focusTrap);

      return () => {
        focusStack.removeFocusTrap(focusTrap);
      };
    }
  }, [trapFocus, props.open, focusStack, internalOpen]);

  useEffect(() => {
    if (!trapFocus) {
      return;
    }

    if (
      props.open !== undefined ? props.open === false : internalOpen === false
    ) {
      if (returnFocusElement && document.activeElement !== returnFocusElement) {
        returnFocusElement.focus();
      }
    }
  }, [trapFocus, props.open, internalOpen, returnFocusElement]);

  return (
    <FocusStackContext.Provider value={focusStack}>
      <Popup
        role="dialog"
        aria-modal="false"
        closeOnEscape
        ref={popupRef}
        {...props}
        onOpen={(e, data) => {
          // Only triggers if modal is uncontrolled (no open prop)
          setInternalOpen(true);
          props.onOpen?.(e, data);
        }}
        onClose={(e, data) => {
          // Only triggers if modal is uncontrolled (no open prop)
          setInternalOpen(false);
          props.onClose?.(e, data);
        }}
      />
    </FocusStackContext.Provider>
  );
};

export { AccessiblePopup };
